import { createSlice } from '@reduxjs/toolkit';
import initialState from './initialState';
import {
  fetchBranches,
  fetchTeams,
  createTeam,
  createBranch,
  updateBranch,
  fetchBranch,
  deleteBranch,
  deleteTeam,
  updateTeam,
  fetchTeam,
  fetchPermissionGroups,
  searchAgents,
  fetchBranchMembers,
  fetchTeamMembers,
} from './services';

export const BranchesAndTeamsSlice = createSlice({
  name: 'BranchesAndTeams',
  initialState,
  reducers: {
    setActiveTab(state, action) {
      state.activeTab = action.payload;
    },
    resetTeamCreation(state) {
      state.teamCreation = { isLoading: false, error: null };
    },
    resetBranchCreation(state) {
      state.branchCreation = { isLoading: false, error: null };
    },
    resetBranchEdit(state) {
      state.branchEdit = { isLoading: false, error: null };
    },
    resetBranchDetail(state) {
      state.branchDetail = {
        data: null,
        requestedId: null,
        isLoading: true,
        error: null,
      };
    },
    resetBranchDeletion(state) {
      state.branchDeletion = { isLoading: false, error: null };
    },
    resetTeamDeletion(state) {
      state.teamDeletion = { isLoading: false, error: null };
    },
    resetTeamEdit(state) {
      state.teamEdit = { isLoading: false, error: null };
    },
    resetBranchMembers(state) {
      state.branchMembers = {
        branchId: null,
        items: [],
        isLoading: true,
        error: null,
      };
    },
    resetTeamMembers(state) {
      state.teamMembers = {
        teamId: null,
        items: [],
        isLoading: true,
        error: null,
      };
    },
    resetTeamDetail(state) {
      state.teamDetail = {
        data: null,
        requestedId: null,
        isLoading: true,
        error: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBranches.pending, (state) => {
        if (!state.branches.items.length) {
          state.branches.isLoading = true;
        }
        state.branches.error = null;
      })
      .addCase(fetchBranches.fulfilled, (state, action) => {
        state.branches.items = action.payload?.results || [];
        state.branches.isLoading = false;
        state.branches.error = null;
      })
      .addCase(fetchBranches.rejected, (state, action) => {
        state.branches.isLoading = false;
        if (state.branches.items.length) {
          return;
        }
        state.branches.error =
          action.error?.message || 'Failed to fetch branches';
      })
      .addCase(fetchTeams.pending, (state) => {
        if (!state.teams.items.length) {
          state.teams.isLoading = true;
        }
        state.teams.error = null;
      })
      .addCase(fetchTeams.fulfilled, (state, action) => {
        state.teams.items = action.payload?.results || [];
        state.teams.isLoading = false;
        state.teams.error = null;
      })
      .addCase(fetchTeams.rejected, (state, action) => {
        state.teams.isLoading = false;
        if (state.teams.items.length) {
          return;
        }
        state.teams.error = action.error?.message || 'Failed to fetch teams';
      })
      .addCase(createTeam.pending, (state) => {
        state.teamCreation.isLoading = true;
        state.teamCreation.error = null;
      })
      .addCase(createTeam.fulfilled, (state, action) => {
        state.teams.items = [...state.teams.items, action.payload];
        state.teamCreation.isLoading = false;
        state.teamCreation.error = null;
      })
      .addCase(createTeam.rejected, (state, action) => {
        state.teamCreation.isLoading = false;
        state.teamCreation.error =
          action.error?.message || 'Failed to create team';
      })
      .addCase(createBranch.pending, (state) => {
        state.branchCreation.isLoading = true;
        state.branchCreation.error = null;
      })
      .addCase(createBranch.fulfilled, (state, action) => {
        state.branches.items = [...state.branches.items, action.payload];
        state.branchCreation.isLoading = false;
        state.branchCreation.error = null;
      })
      .addCase(createBranch.rejected, (state, action) => {
        state.branchCreation.isLoading = false;
        state.branchCreation.error =
          action.error?.message || 'Failed to create branch';
      })
      .addCase(updateBranch.pending, (state) => {
        state.branchEdit.isLoading = true;
        state.branchEdit.error = null;
      })
      .addCase(updateBranch.fulfilled, (state, action) => {
        state.branches.items = state.branches.items.map((item) =>
          item.id === action.payload.id ? { ...item, ...action.payload } : item
        );
        state.branchEdit.isLoading = false;
        state.branchEdit.error = null;
      })
      .addCase(updateBranch.rejected, (state, action) => {
        state.branchEdit.isLoading = false;
        state.branchEdit.error =
          action.error?.message || 'Failed to update branch';
      })
      .addCase(fetchBranch.pending, (state, action) => {
        state.branchDetail.requestedId = action.meta.arg;
        state.branchDetail.data = null;
        state.branchDetail.isLoading = true;
        state.branchDetail.error = null;
      })
      .addCase(fetchBranch.fulfilled, (state, action) => {
        if (action.meta.arg !== state.branchDetail.requestedId) return;
        state.branchDetail.data = action.payload;
        state.branchDetail.isLoading = false;
        state.branchDetail.error = null;
      })
      .addCase(fetchBranch.rejected, (state, action) => {
        if (action.meta.arg !== state.branchDetail.requestedId) return;
        state.branchDetail.isLoading = false;
        state.branchDetail.error =
          action.error?.message || 'Failed to fetch branch';
      })
      .addCase(deleteBranch.pending, (state) => {
        state.branchDeletion.isLoading = true;
        state.branchDeletion.error = null;
      })
      .addCase(deleteBranch.fulfilled, (state, action) => {
        const deletedId = action.meta.arg?.branchId;
        state.branches.items = state.branches.items.filter(
          (item) => item.id !== deletedId
        );
        state.branchDeletion.isLoading = false;
        state.branchDeletion.error = null;
      })
      .addCase(deleteBranch.rejected, (state, action) => {
        state.branchDeletion.isLoading = false;
        state.branchDeletion.error =
          action.error?.message || 'Failed to delete branch';
      })
      .addCase(deleteTeam.pending, (state) => {
        state.teamDeletion.isLoading = true;
        state.teamDeletion.error = null;
      })
      .addCase(deleteTeam.fulfilled, (state, action) => {
        const deletedId = action.meta.arg;
        state.teams.items = state.teams.items.filter(
          (item) => item.id !== deletedId
        );
        state.teamDeletion.isLoading = false;
        state.teamDeletion.error = null;
      })
      .addCase(deleteTeam.rejected, (state, action) => {
        state.teamDeletion.isLoading = false;
        state.teamDeletion.error =
          action.error?.message || 'Failed to delete team';
      })
      .addCase(updateTeam.pending, (state) => {
        state.teamEdit.isLoading = true;
        state.teamEdit.error = null;
      })
      .addCase(updateTeam.fulfilled, (state, action) => {
        state.teams.items = state.teams.items.map((item) =>
          item.id === action.payload.id ? { ...item, ...action.payload } : item
        );
        state.teamEdit.isLoading = false;
        state.teamEdit.error = null;
      })
      .addCase(updateTeam.rejected, (state, action) => {
        state.teamEdit.isLoading = false;
        state.teamEdit.error = action.error?.message || 'Failed to update team';
      })
      .addCase(fetchTeam.pending, (state, action) => {
        state.teamDetail.requestedId = action.meta.arg;
        state.teamDetail.data = null;
        state.teamDetail.isLoading = true;
        state.teamDetail.error = null;
      })
      .addCase(fetchTeam.fulfilled, (state, action) => {
        if (action.meta.arg !== state.teamDetail.requestedId) return;
        state.teamDetail.data = action.payload;
        state.teamDetail.isLoading = false;
        state.teamDetail.error = null;
      })
      .addCase(fetchTeam.rejected, (state, action) => {
        if (action.meta.arg !== state.teamDetail.requestedId) return;
        state.teamDetail.isLoading = false;
        state.teamDetail.error =
          action.error?.message || 'Failed to fetch team';
      })
      .addCase(fetchPermissionGroups.pending, (state) => {
        state.permissionGroups.isLoading = true;
        state.permissionGroups.error = null;
      })
      .addCase(fetchPermissionGroups.fulfilled, (state, action) => {
        state.permissionGroups.items = action.payload?.results || [];
        state.permissionGroups.isLoading = false;
        state.permissionGroups.error = null;
      })
      .addCase(fetchPermissionGroups.rejected, (state, action) => {
        state.permissionGroups.isLoading = false;
        state.permissionGroups.error =
          action.error?.message || 'Failed to fetch permission groups';
      })
      .addCase(searchAgents.pending, (state, action) => {
        const { q } = action.meta.arg || {};
        state.agentsSearch.query = q || '';
        state.agentsSearch.isLoading = true;
        state.agentsSearch.error = null;
        state.agentsSearch.items = [];
      })
      .addCase(searchAgents.fulfilled, (state, action) => {
        if ((action.meta.arg?.q || '') !== state.agentsSearch.query) return;
        state.agentsSearch.items = action.payload?.users || [];
        state.agentsSearch.isLoading = false;
        state.agentsSearch.error = null;
      })
      .addCase(searchAgents.rejected, (state, action) => {
        if ((action.meta.arg?.q || '') !== state.agentsSearch.query) return;
        state.agentsSearch.isLoading = false;
        state.agentsSearch.error =
          action.error?.message || 'Failed to search agents';
      })
      .addCase(fetchBranchMembers.pending, (state, action) => {
        state.branchMembers.branchId = action.meta.arg;
        state.branchMembers.items = [];
        state.branchMembers.isLoading = true;
        state.branchMembers.error = null;
      })
      .addCase(fetchBranchMembers.fulfilled, (state, action) => {
        if (Number(action.meta.arg) !== Number(state.branchMembers.branchId)) {
          return;
        }
        state.branchMembers.items = action.payload?.results || [];
        state.branchMembers.isLoading = false;
        state.branchMembers.error = null;
      })
      .addCase(fetchBranchMembers.rejected, (state, action) => {
        if (Number(action.meta.arg) !== Number(state.branchMembers.branchId)) {
          return;
        }
        state.branchMembers.isLoading = false;
        state.branchMembers.error =
          action.error?.message || 'Failed to fetch branch members';
      })
      .addCase(fetchTeamMembers.pending, (state, action) => {
        state.teamMembers.teamId = action.meta.arg;
        state.teamMembers.items = [];
        state.teamMembers.isLoading = true;
        state.teamMembers.error = null;
      })
      .addCase(fetchTeamMembers.fulfilled, (state, action) => {
        if (Number(action.meta.arg) !== Number(state.teamMembers.teamId)) {
          return;
        }
        state.teamMembers.items = action.payload?.results || [];
        state.teamMembers.isLoading = false;
        state.teamMembers.error = null;
      })
      .addCase(fetchTeamMembers.rejected, (state, action) => {
        if (Number(action.meta.arg) !== Number(state.teamMembers.teamId)) {
          return;
        }
        state.teamMembers.isLoading = false;
        state.teamMembers.error =
          action.error?.message || 'Failed to fetch team members';
      });
  },
});

export const {
  setActiveTab,
  resetTeamCreation,
  resetBranchCreation,
  resetBranchEdit,
  resetBranchDetail,
  resetBranchDeletion,
  resetTeamDeletion,
  resetTeamEdit,
  resetTeamDetail,
  resetBranchMembers,
  resetTeamMembers,
} = BranchesAndTeamsSlice.actions;

export const selectActiveTab = (state) => state.BranchesAndTeamsSlice.activeTab;
export const selectBranches = (state) => state.BranchesAndTeamsSlice.branches;
export const selectTeams = (state) => state.BranchesAndTeamsSlice.teams;
export const selectTeamCreation = (state) =>
  state.BranchesAndTeamsSlice.teamCreation;
export const selectBranchCreation = (state) =>
  state.BranchesAndTeamsSlice.branchCreation;
export const selectBranchEdit = (state) =>
  state.BranchesAndTeamsSlice.branchEdit;
export const selectBranchDetail = (state) =>
  state.BranchesAndTeamsSlice.branchDetail;
export const selectBranchDeletion = (state) =>
  state.BranchesAndTeamsSlice.branchDeletion;
export const selectTeamDeletion = (state) =>
  state.BranchesAndTeamsSlice.teamDeletion;
export const selectTeamEdit = (state) => state.BranchesAndTeamsSlice.teamEdit;
export const selectTeamDetail = (state) =>
  state.BranchesAndTeamsSlice.teamDetail;
export const selectPermissionGroups = (state) =>
  state.BranchesAndTeamsSlice.permissionGroups;
export const selectAgentsSearch = (state) =>
  state.BranchesAndTeamsSlice.agentsSearch;
export const selectBranchMembers = (state) =>
  state.BranchesAndTeamsSlice.branchMembers;
export const selectTeamMembers = (state) =>
  state.BranchesAndTeamsSlice.teamMembers;

export default BranchesAndTeamsSlice.reducer;
